import { ActionButton } from '@/common/buttons/ActionButton';
import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { GridConstants } from '@/common/grid/GridConstants';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { ObjUtils } from '@/common/utils/ObjUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { Box } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

export const PriceResList = ({ progId }) => {
  const rsrc = useRsrc();
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [tierLookupReady, setTierLookupReady] = useState(false);

  const tierNumByIdRef = useRef({});
  //need to use the prog id to first get prog prods, then can use these to get the
  //actual relevant tiers to be matched to the price ress
  useEffect(() => {
    if (!progId) {
      setTierLookupReady(true);
      return;
    }

    rsrc.rows('md', 'PROG_PROD', { expr: ExprUtils.whereParams('progId', progId) }).then((progProds) => {
      const progProdIds = (progProds ?? []).map((progProd) => progProd.id);
      if (!progProdIds.length) {
        setTierLookupReady(true);
        return;
      }

      return rsrc
        .rows('md', 'PROG_PROD_TIER', {
          query: 'TIER_LIST',
          expr: ExprUtils.whereParams('progProdId', progProdIds),
        })
        .then((tiers) => {
          tierNumByIdRef.current = Object.fromEntries((tiers ?? []).map((tier) => [tier.id, tier.tier]));
          setTierLookupReady(true);
        });
    });
  }, [progId, rsrc]);

  const handleSelectionChanged = (event) => {
    setSelectedEntities(event.api.getSelectedNodes().map((node) => node.data));
  };

  //allows for filtering on the tier number
  const clientSideRowData = useCallback(
    (setDataSource) => {
      if (!progId) {
        setDataSource([]);
        return;
      }

      rsrc.rows('md', 'PRICE_RES', { expr: ExprUtils.whereParams('progId', progId) }).then((rows) => {
        rows ??= [];

        setDataSource(
          rows.map((row) => ({
            ...row,
            tierNum: tierNumByIdRef.current[row.tierId] ?? null,
            ovrdTierNum: tierNumByIdRef.current[row.ovrdTierId] ?? null,
          })),
        );
      });
    },
    [progId, rsrc],
  );

  const augFieldDefs = useCallback((fieldToFieldDef) => {
    fieldToFieldDef.checkbox = GridConstants.checkbox;

    fieldToFieldDef.tierFormula.displayName = 'Formula';
    fieldToFieldDef.tierFormula.type = 'PRICE_RES_FORMULA';
    fieldToFieldDef.tierFormula.minWidth = 600;

    fieldToFieldDef.conc.type = 'PRICE_RES_CONC';

    delete fieldToFieldDef.tierVars;

    delete fieldToFieldDef.tierId;
    delete fieldToFieldDef.ovrdTierId;

    fieldToFieldDef.tierNum = {
      field: 'tierNum',
      displayName: 'Tier',
      type: 'PRICE_RES_TIER',
      filter: 'agNumberColumnFilter',
      idField: 'tierId',
      module: 'md',
      entity: 'PROG_PROD_TIER',
      decimalPrecision: 0,
      minDecimalPrecision: 0,
    };
    fieldToFieldDef.ovrdTierNum = {
      field: 'ovrdTierNum',
      displayName: 'Override Tier',
      type: 'PRICE_RES_TIER',
      filter: 'agNumberColumnFilter',
      idField: 'ovrdTierId',
      module: 'md',
      entity: 'PROG_PROD_TIER',
      decimalPrecision: 0,
      minDecimalPrecision: 0,
    };
  }, []);

  const augTabButtons = (tabButtons) => {
    ObjUtils.clearObj(tabButtons);
    const isAdjTierDisabled = !selectedEntities.length;
    tabButtons.push(
      <ActionButton data={incData} toolTip="Increase Tier" icon={fas.faPlus} disabled={isAdjTierDisabled} />,
      <ActionButton data={decData} toolTip="Decrease Tier" icon={fas.faMinus} disabled={isAdjTierDisabled} />,
    );
  };

  const incData = {
    title: 'Increase Tier',
    entity: 'PRICE_RES',
    addlReqData: {
      progId: progId,
      selectedEntities: selectedEntities,
      incDec: 'INC',
    },

    pluginCode: 'ADJ_TIER_ACTION_PLUGIN',
  };

  const decData = {
    title: 'Decrease Tier',
    entity: 'PRICE_RES',
    addlReqData: {
      progId: progId,
      selectedEntities: selectedEntities,
      incDec: 'DEC',
    },
    pluginCode: 'ADJ_TIER_ACTION_PLUGIN',
  };

  if (!tierLookupReady) return <></>;

  // Overlays the empty left side of the tab button row; fixed width so it doesn't stretch under the buttons
  return (
    <Box sx={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <EntityList
        entity="PRICE_RES"
        clientSideRowData={clientSideRowData}
        augTabButtons={augTabButtons}
        augFieldDefs={augFieldDefs}
        gridOptions={{ rowSelection: 'multiple' }}
        events={{ onSelectionChanged: handleSelectionChanged }}
      />
    </Box>
  );
};
